import React from 'react';

/**
 * PageHeader — ページ題目領域
 * ------------------------------------------------------------------
 * ページ上部の「タイトル + 補助説明 + 右側アクション領域」を統一する共通ヘッダー。
 * ほぼ全ての管理ページに共通する視覚パターンの抽出（Ph.3）。
 *
 * 設計方針:
 *   - 見出しは意味的な <h1>/<h2>… を出力（level で制御）。デフォルトは h2。
 *   - フォントサイズ・余白・色は tokens.css の CSS 変数のみ使用（HEX 禁止）。
 *   - 右側 extra にはボタン・StatusBadge 等を自由に配置可能。
 *   - extra 内の操作要素のフォーカスリングは tokens.css のグローバル
 *     :focus-visible に委ねる（個別に outline を消さない）。
 *
 * @param {object}      props
 * @param {React.ReactNode} props.title               ページタイトル（必須）
 * @param {React.ReactNode} [props.subtitle]          タイトル下の補助説明
 * @param {React.ReactNode} [props.extra]             右側のアクション領域（Button / StatusBadge 等）
 * @param {2|3|4}       [props.level=2]               見出しレベル（h2〜h4）。視覚サイズも連動
 * @param {boolean}     [props.divider=false]         true で下端に区切り罫線を表示
 * @param {React.CSSProperties} [props.style]         追加スタイル（マージされる）
 * @param {string}      [props.className]             追加クラス名
 *
 * @example
 * <PageHeader
 *   title="ユーザー管理"
 *   subtitle="管理者権限の付与・変更を行います"
 *   extra={<Button type="primary">新規登録</Button>}
 * />
 */
const PageHeader = ({
  title,
  subtitle,
  extra,
  level = 2,
  divider = false,
  style,
  className,
  ...rest
}) => {
  // 見出しレベルごとのフォントサイズトークン（h2=2xl / h3=xl / h4=lg）
  const titleSizeByLevel = {
    2: 'var(--text-2xl)',
    3: 'var(--text-xl)',
    4: 'var(--text-lg)',
  };
  const HeadingTag = `h${level}`;

  const wrapperStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 'var(--space-4)',
    flexWrap: 'wrap',
    marginBottom: 'var(--space-6)',
    paddingBottom: divider ? 'var(--space-4)' : 0,
    borderBottom: divider ? '1px solid var(--grey-30)' : 'none',
    ...style,
  };

  return (
    <div className={className} style={wrapperStyle} {...rest}>
      <div style={{ minWidth: 0 }}>
        <HeadingTag
          style={{
            margin: 0,
            fontSize: titleSizeByLevel[level] || titleSizeByLevel[2],
            fontWeight: 'var(--font-bold)',
            lineHeight: 'var(--leading-normal)',
            color: 'var(--black)',
          }}
        >
          {title}
        </HeadingTag>
        {subtitle && (
          <div
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--text-sm)',
              lineHeight: 'var(--leading-normal)',
              color: 'var(--grey-65)',
            }}
          >
            {subtitle}
          </div>
        )}
      </div>
      {extra && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            flexShrink: 0,
          }}
        >
          {extra}
        </div>
      )}
    </div>
  );
};

export default PageHeader;
